import { useContext } from "react";
import { intl } from "LocaleWrapper";
import { Menu, MenuItemProps } from "@cofano/cck";
import { convertedIcon } from "components/icon/IconUtils";
import { hasPluginPermission } from "imports/Utils";
import { LayoutContext } from "@cofano/cck/Layout/Context";
import { useQuery } from "@apollo/client";
import { useStoreState } from "ezStore";
import { ChildPropsFromArray } from "types/commons";
import {
	GetNavigationDocument,
	GetNavigationQuery,
} from "__generated__/components/layout/graphql/layout.queries.generated";
import { NavigationUrlParameter } from "__generated__/enums";

type NavigationItem = Omit<ChildPropsFromArray<GetNavigationQuery, "navigation">, "children"> & {
	children?: ChildPropsFromArray<GetNavigationQuery, "navigation">["children"];
};

const Navigation = ({ path, navigationItems, disableNavigation = false }: {
	path: string,
	navigationItems?: NavigationItem[],
	disableNavigation?: boolean,
}) => {

	const session = useStoreState(state => state.session);

	const { data } = useQuery(GetNavigationDocument, {
		skip: !!navigationItems,
	});

	const createMenuItem = (item: NavigationItem): MenuItemProps => {
		return {
			label: intl.formatMessage({ id: item.languageKey }),
			icon: item.icon ? <i className={`fa ${convertedIcon(item.icon)}`} /> : undefined,
			url: parameterizeUrl(item.url, item.parameter) ?? undefined,
			hidden: !isMenuItemVisible(item),
			items: item.children && item.children.length > 0
				? item.children.map(createMenuItem)
				: undefined,
		};
	};

	const parameterizeUrl = (url: string | null | undefined, parameter: NavigationUrlParameter | null | undefined) => {
		if (!url || !parameter) {
			return url;
		}

		switch (parameter) {
			case NavigationUrlParameter.Customer:
			case NavigationUrlParameter.Operator: {
				if (session.user?.properties.relationId) {
					return url.replace("_PARAMETER_", String(session.user.properties.relationId));
				}
				break;
			}
			case NavigationUrlParameter.Ship: {
				if (session.user?.properties.shipIds?.[0]) {
					return url.replace("_PARAMETER_", String(session.user.properties.shipIds[0]));
				}
				break;
			}
		}

		return url;
	};

	const isMenuItemVisible = (item: NavigationItem): boolean => {
		if (!(item.url || item.children?.length)) {
			return false;
		}

		return item.plugins.every(plugin => hasPluginPermission(session, plugin));
	};

	const menuItems = (data?.navigation ?? navigationItems)?.map(createMenuItem)  ?? [];

	const { navigate: layoutNavigate } = useContext(LayoutContext);

	return (
		<Menu
			items={menuItems}
			match={url => path.startsWith(url)}
			navigate={(url, event) => {
				if (disableNavigation) {
					return;
				}
				if (url.startsWith("http")) {
					window.location.href = url;
				} else {
					layoutNavigate?.(url, event);
				}
			}}
		/>
	);
};

export default Navigation;
